/** GENERATED_ASSET — basin painter: contact shadow, wall, rim and the empty (dry) interior. */
import type { Ctx } from './ctx';
import type { Layer } from './svgKit';
import { Path, rrect } from './pathKit';
import type { Pt } from './pathKit';
import { BASE_Y, CX, GROUND } from './layout';

export function paintContactShadow(ctx: Ctx, L: Layer): void {
  L.ellipse(CX, GROUND - 1.6, 176, 1.6, { fill: ctx.sw.outline, fo: ctx.style.contactShadowOpacity, content: false, part: 'contact-shadow' });
}

/** Wall courses: two rows of staggered blocks; joints are structural so they stay at every detail tier. */
function paintWallBlocks(ctx: Ctx, L: Layer): void {
  const { sw, L: lay } = ctx;
  const b = lay.basin;
  const r = ctx.rng('basin.blocks');
  const rowY = [b.rimBottom, 307.5, BASE_Y];
  for (let row = 0; row < 2; row++) {
    let x = b.x0 + (row ? 30 : 8);
    const y0 = rowY[row]!, y1 = rowY[row + 1]!;
    while (x < b.x1 - 14) {
      const w = r.range(40, 62);
      const xe = Math.min(b.x1 - 2, x + w);
      const tone = r.next();
      if (xe - x > 12 && tone < 0.34) L.poly([{ x, y: y0 }, { x: xe, y: y0 }, { x: xe, y: y1 }, { x, y: y1 }], { fill: sw.stone.light, fo: 0.3, content: true, part: 'block-lit' });
      else if (xe - x > 12 && tone > 0.78) L.poly([{ x, y: y0 }, { x: xe, y: y0 }, { x: xe, y: y1 }, { x, y: y1 }], { fill: sw.stone.shadow, fo: 0.28, part: 'block-dim' });
      if (xe < b.x1 - 12) L.line(xe, y0, xe, y1, { stroke: sw.stone.seam, sw: ctx.style.seamStroke, so: 0.75, part: 'joint' });
      x = xe;
    }
  }
  L.line(b.x0 + 4, 307.5, b.x1 - 4, 307.5, { stroke: sw.stone.seam, sw: ctx.style.seamStroke, so: 0.75, part: 'course' });
}

export function paintBasin(ctx: Ctx, L: Layer): void {
  const { sw, style, L: lay } = ctx;
  const b = lay.basin;
  // Wall (front face + rounded tub ends).
  L.path(rrect(b.x0, b.wallTop, b.x1, BASE_Y, [0, 0, 4, 4]), { fill: sw.stone.base, stroke: sw.outline, sw: style.outerStroke, part: 'basin-wall' });
  L.poly([{ x: b.x0 + 1, y: BASE_Y - 5 }, { x: b.x1 - 1, y: BASE_Y - 5 }, { x: b.x1 - 1, y: BASE_Y - 0.5 }, { x: b.x0 + 1, y: BASE_Y - 0.5 }],
    { fill: sw.stone.deep, fo: 0.3, part: 'wall-foot' });
  // End shading on the tub's rounded corners.
  L.poly([{ x: b.x0 + 1, y: b.wallTop }, { x: b.x0 + 16, y: b.rimBottom }, { x: b.x0 + 16, y: BASE_Y - 0.5 }, { x: b.x0 + 1, y: BASE_Y - 0.5 }], { fill: sw.stone.shadow, fo: 0.3, part: 'end-left' });
  L.poly([{ x: b.x1 - 1, y: b.wallTop }, { x: b.x1 - 16, y: b.rimBottom }, { x: b.x1 - 16, y: BASE_Y - 0.5 }, { x: b.x1 - 1, y: BASE_Y - 0.5 }], { fill: sw.stone.shadow, fo: 0.38, part: 'end-right' });
  paintWallBlocks(ctx, L);
  // Rim top face.
  L.path(rrect(b.x0, b.rimTop, b.x1, b.rimBottom, b.rimR), { fill: sw.stone.light, stroke: sw.outline, sw: style.outerStroke, part: 'rim' });
  paintRimSeams(ctx, L);
  // Opening: dry floor + back wall. Water is a separate layer painted over this.
  L.path(rrect(b.openX0, b.openY0, b.openX1, b.openY1, b.openR), { fill: sw.stone.deep, stroke: sw.outline, sw: 1.6, part: 'basin-floor' });
  L.path(rrect(b.openX0 + 0.8, b.openY0 + 0.8, b.openX1 - 0.8, b.openY0 + 14, [b.openR - 1, b.openR - 1, 0, 0]), { fill: sw.stone.shadow, part: 'back-wall' });
  L.line(b.openX0 + 4, b.openY0 + 14, b.openX1 - 4, b.openY0 + 14, { stroke: sw.stone.seam, sw: 1, so: 0.6, part: 'back-wall-base' });
  // Inner bevel highlight along the near lip.
  L.line(b.openX0 + 12, b.openY1 + 1.6, b.openX1 - 12, b.openY1 + 1.6, { stroke: sw.stone.lit, sw: 1.1, so: 0.85, part: 'lip-light' });
  if (ctx.v.brokenRim) paintRimBreak(ctx, L);
}

function paintRimSeams(ctx: Ctx, L: Layer): void {
  const { sw, L: lay } = ctx;
  const b = lay.basin;
  const r = ctx.rng('basin.rim');
  const seam = { stroke: sw.stone.seam, sw: ctx.style.seamStroke, so: 0.7, part: 'rim-seam' } as const;
  for (let x = b.x0 + 36; x < b.x1 - 30; x += r.range(34, 52)) {
    L.line(x, b.openY1 + 0.6, x + r.range(-1, 1), b.rimBottom - 0.8, seam);
    L.line(x + r.range(-5, 5), b.rimTop + 0.8, x + r.range(-1, 1), b.openY0 - 0.6, seam);
  }
  L.line(b.x0 + 4, b.openY0 + 7, b.openX0 - 0.8, b.openY0 + 7, seam);
  L.line(b.openX1 + 0.8, b.openY0 + 7, b.x1 - 4, b.openY0 + 7, seam);
  // Subtle rim highlight (top-left light).
  L.path(new Path().poly([{ x: b.x0 + 14, y: b.rimTop + 2.2 }, { x: b.x1 - 30, y: b.rimTop + 2.2 }], false), { stroke: sw.stone.lit, sw: 1.3, so: 0.7, part: 'rim-light' });
}

/** dry_ruin: a section of front rim has collapsed, showing the cut stone below. */
function paintRimBreak(ctx: Ctx, L: Layer): void {
  const { sw, L: lay } = ctx;
  const b = lay.basin;
  const x = b.x1 - 92;
  const pts: Pt[] = [
    { x, y: b.openY1 + 1.5 }, { x: x + 9, y: b.openY1 + 5 }, { x: x + 14, y: b.rimBottom - 2.5 }, { x: x + 27, y: b.openY1 + 6 },
    { x: x + 38, y: b.rimBottom - 1 }, { x: x + 46, y: b.openY1 + 1.5 },
  ];
  L.poly(pts, { fill: sw.stone.shadow, stroke: sw.stone.seam, sw: 1.2, part: 'rim-break' });
  L.poly([{ x: x + 11, y: b.rimBottom - 3 }, { x: x + 16, y: b.rimBottom + 0.5 }, { x: x + 24, y: b.rimBottom - 1.5 }, { x: x + 26, y: b.openY1 + 7 }, { x: x + 20, y: b.openY1 + 5.5 }],
    { fill: sw.stone.deep, fo: 0.55, part: 'rim-break-dark' });
}
